import { useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useToastState, type Toast } from '@/hooks/useToast';

const icons = {
  success: <CheckCircle className="h-4 w-4 text-green-500" />,
  error: <XCircle className="h-4 w-4 text-red-500" />,
  warning: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
  default: <Info className="h-4 w-4 text-blue-500" />,
};

function ToastItem({ toast }: { toast: Toast }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-floating',
        'animate-slide-up w-80 max-w-full',
        toast.variant === 'error' && 'border-red-200',
        toast.variant === 'success' && 'border-green-200',
        toast.variant === 'warning' && 'border-yellow-200',
        toast.variant === 'default' && 'border-slate-200',
      )}
    >
      <div className="mt-0.5 shrink-0">{icons[toast.variant]}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-xs text-slate-500">{toast.description}</p>
        )}
      </div>
    </div>
  );
}

export function ToastContainer() {
  const { currentToasts, subscribe } = useToastState();

  useEffect(() => {
    const unsub = subscribe();
    return () => { unsub; };
  }, [subscribe]);

  if (currentToasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 items-end">
      {currentToasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
